/* src/pages/ResetPassword.jsx
 * In-app password reset via oobCode from branded Postmark email
 */

import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../firebase';
import '../styles/auth.css';

function ResetShell({ children }) {
  return (
    <div className="auth-page">
      <div className="auth-wrap">
        <a href="https://gocta.ai" className="auth-wordmark">gocta<span className="accent">.ai</span></a>
        <div className="auth-card">{children}</div>
      </div>
    </div>
  );
}

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const oobCode = searchParams.get('oobCode');
  const checkedRef = useRef(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [checking, setChecking] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!oobCode) {
      setChecking(false);
      setError('Missing reset code. Request a new password reset from the login page.');
      return;
    }
    if (checkedRef.current === oobCode) return;
    checkedRef.current = oobCode;

    (async () => {
      try {
        const accountEmail = await verifyPasswordResetCode(auth, oobCode);
        setEmail(accountEmail);
        setChecking(false);
      } catch (err) {
        console.error('Invalid password reset code:', err);
        setError('This password reset link is invalid or has expired. Please request a new one.');
        setChecking(false);
      }
    })();
  }, [oobCode]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!oobCode) return;

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setSuccess(true);
      window.history.replaceState({}, document.title, '/reset-password');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      console.error('Password reset failed:', err);
      setError('Failed to reset password. The link may have expired — request a new one from login.');
    } finally {
      setSubmitting(false);
    }
  }

  if (checking) {
    return (
      <ResetShell>
        <span className="inbox-chip" style={{ marginBottom: '1rem', display: 'inline-block' }}>Reset</span>
        <h1 className="auth-title">Checking reset link</h1>
        <p className="auth-subtitle">Please wait…</p>
      </ResetShell>
    );
  }

  if (success) {
    return (
      <ResetShell>
        <span className="inbox-chip" style={{ marginBottom: '1rem', display: 'inline-block' }}>Done</span>
        <h1 className="auth-title" style={{ color: 'var(--accent-ink)' }}>Password updated ✓</h1>
        <div className="auth-success">Redirecting you to log in…</div>
      </ResetShell>
    );
  }

  if (error && !email) {
    return (
      <ResetShell>
        <span className="inbox-chip" style={{ marginBottom: '1rem', display: 'inline-block' }}>Link expired</span>
        <h1 className="auth-title">Reset link expired</h1>
        <div className="auth-error" style={{ marginBottom: '1.5rem' }}>{error}</div>
        <p className="auth-footer" style={{ marginTop: 0 }}>
          <Link to="/login" className="auth-link">Back to login to request a new link</Link>
        </p>
      </ResetShell>
    );
  }

  return (
    <ResetShell>
      <span className="inbox-chip" style={{ marginBottom: '1rem', display: 'inline-block' }}>Reset password</span>
      <h1 className="auth-title">Choose a new password</h1>
      {email && (
        <p className="auth-subtitle" style={{ marginBottom: '1.5rem' }}>
          For <strong>{email}</strong>
        </p>
      )}

      {error && <div className="auth-error">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="auth-field">
          <label className="auth-label">New password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            className="auth-input"
            autoComplete="new-password"
          />
        </div>
        <div className="auth-field">
          <label className="auth-label">Confirm password</label>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={6}
            className="auth-input"
            autoComplete="new-password"
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="auth-btn-primary"
          style={{ opacity: submitting ? 0.6 : 1 }}
        >
          {submitting ? 'Updating…' : 'Update password'}
        </button>
      </form>

      <p className="auth-footer">
        <Link to="/login" className="auth-link">Back to login</Link>
      </p>
    </ResetShell>
  );
}
